import { Alert, Button, TextField, Typography } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";

import AppDialog from "components/common/AppDialog";

import { confirmPhraseMatches } from "./confirmPhrase";

interface TypeToConfirmDialogProps {
  open: boolean;
  title: string;
  /** What goes, in bold above the message (a project's name, say). */
  subject?: ReactNode;
  children?: ReactNode;
  confirmLabel?: string;
  /** When set, the confirm button stays disabled until this exact phrase is typed. */
  confirmPhrase?: string | null;
  phraseLabel?: string;
  phrasePlaceholder?: string;
  onCancel: () => void;
  onConfirm: () => void;
  confirmDisabled?: boolean;
  busy?: boolean;
  /** Why the last attempt failed. The dialog stays open so the reason is read. */
  error?: string | null;
}

/** A destructive confirmation that can ask for a name to be typed first. */
export default function TypeToConfirmDialog({
  open,
  title,
  subject,
  children,
  confirmLabel = "Delete",
  confirmPhrase,
  phraseLabel,
  phrasePlaceholder,
  onCancel,
  onConfirm,
  confirmDisabled = false,
  busy = false,
  error = null,
}: TypeToConfirmDialogProps) {
  const [typed, setTyped] = useState("");
  // What was typed for one target never carries over to the next.
  useEffect(() => {
    if (open) setTyped("");
  }, [open, confirmPhrase]);
  const phraseTyped =
    confirmPhrase == null || confirmPhraseMatches(typed, confirmPhrase);

  return (
    <AppDialog
      open={open}
      onClose={() => !busy && onCancel()}
      title={title}
      actions={
        <>
          <Button onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={confirmDisabled || busy || !phraseTyped}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {subject ? (
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
          {subject}
        </Typography>
      ) : null}
      {children}
      {confirmPhrase != null ? (
        <TextField
          autoFocus
          fullWidth
          required
          size="small"
          sx={{ mt: 2 }}
          label={phraseLabel ?? `Type "${confirmPhrase}" to confirm`}
          placeholder={phrasePlaceholder}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          disabled={busy}
          inputProps={{ autoComplete: "off", spellCheck: false }}
        />
      ) : null}
      {error ? (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      ) : null}
    </AppDialog>
  );
}
